import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CurrentUser,
  RequestOtp,
  UserVerifyOTP,
} from "../../../utils/apiCalls/auth";
import { UpdateHealthProfessionalProfile } from "../../../utils/apiCalls/healthProfessionals";
import { useToast } from "../../../utils/ToastContext";

const initialValues = {
  name: "",
  userName: "",
  prefix: "",
  mobileNumber: "",
  email: "",
  password: "",
  workplace: "",
  position: "",
  description: "",
  numberOfUsers: "",
};

const useUserForm = () => {
  const [errors, setErrors] = useState(initialValues);
  const [formData, setFormData] = useState(initialValues);
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const [originalFormData, setOriginalFormData] = useState(initialValues);
  const [originalSecondaryEmails, setOriginalSecondaryEmails] = useState([]);
  const [newSecondaryEmail, setNewSecondaryEmail] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [resetPassword, setResetPassword] = useState(false);
  const profilePayloadRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileDetailsToFormById();
  }, []);

  const fetchProfileDetailsToFormById = async () => {
    try {
      const res = await CurrentUser();
      const { data, message, statusCode } = res;
      if (statusCode === 200) {
        const initialData = {
          name: data.currentHealthProfessionalModel.name,
          userName: data.currentHealthProfessionalModel.userName,
          prefix: data.currentHealthProfessionalModel.prefix,
          mobileNumber: data.currentHealthProfessionalModel.mobileNumber,
          email: data.currentHealthProfessionalModel.email,
          // Never prefill with the backend's masked/placeholder password —
          // leave blank so an untouched field can't get resubmitted as the
          // real password, and so the show/hide toggle only ever reveals
          // what the user actually typed.
          password: "",
          workplace: data.currentHealthProfessionalModel.workPlace,
          position: data.currentHealthProfessionalModel.position,
          description: data.currentHealthProfessionalModel.description,
          numberOfUsers: data.currentHealthProfessionalModel.numberOfUsers
            ? data.currentHealthProfessionalModel.numberOfUsers.toString()
            : "",
        };
        const initialEmails = data.currentHealthProfessionalModel?.emails || [];
        setFormData(initialData);
        setSecondaryEmails(initialEmails);
        setOriginalFormData({ ...initialData });
        setOriginalSecondaryEmails([...initialEmails]);
      } else {
        console.error("Error fetching profile details by ID:", message);
      }
    } catch (error) {
      console.error("Error fetching profile details by ID:", error?.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleAddSecondaryEmail = () => {
    if (!newSecondaryEmail.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(newSecondaryEmail)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    if (secondaryEmails.includes(newSecondaryEmail)) {
      showToast("This email already exists", "error");
      return;
    }

    setSecondaryEmails([...secondaryEmails, newSecondaryEmail]);
    setNewSecondaryEmail("");
  };

  const handleDeleteSecondaryEmail = (index) => {
    setSecondaryEmails(secondaryEmails.filter((_, i) => i !== index));
  };

  const handleEditSecondaryEmail = (index) => {
    setEditingIndex(index);
    setEditingEmail(secondaryEmails[index]);
  };

  const handleSaveEditSecondaryEmail = (index) => {
    if (!editingEmail.trim()) {
      showToast("Please enter an email address", "error");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(editingEmail)) {
      showToast("Please enter a valid email address", "error");
      return;
    }

    const isDuplicate = secondaryEmails.some(
      (email, i) => i !== index && email === editingEmail,
    );
    if (isDuplicate) {
      showToast("This email already exists", "error");
      return;
    }

    const updatedEmails = [...secondaryEmails];
    updatedEmails[index] = editingEmail;
    setSecondaryEmails(updatedEmails);
    setEditingIndex(null);
    setEditingEmail("");
  };

  const handleCancelEditSecondaryEmail = () => {
    setEditingIndex(null);
    setEditingEmail("");
  };

  const onResetPasswordClick = () => {
    setResetPassword((prev) => !prev);
    // Either entering edit mode (start blank) or cancelling out of it
    // (drop whatever was typed) - both cases want an empty field.
    setFormData((prev) => ({ ...prev, password: "" }));
    setErrors((prev) => ({ ...prev, password: "" }));
  };

  const isFormChanged = () => {
    // Helper function to safely get trimmed string
    const getTrimmed = (val) => (val ? String(val).trim() : "");

    // Check if any form field has changed
    const nameChanged =
      getTrimmed(formData.name) !== getTrimmed(originalFormData.name);
    const userNameChanged =
      getTrimmed(formData.userName) !== getTrimmed(originalFormData.userName);
    const prefixChanged =
      getTrimmed(formData.prefix) !== getTrimmed(originalFormData.prefix);
    const mobileChanged =
      getTrimmed(formData.mobileNumber) !==
      getTrimmed(originalFormData.mobileNumber);
    const emailChanged =
      getTrimmed(formData.email) !== getTrimmed(originalFormData.email);
    const passwordChanged =
      getTrimmed(formData.password) !== getTrimmed(originalFormData.password);
    const workplaceChanged =
      getTrimmed(formData.workplace) !== getTrimmed(originalFormData.workplace);
    const positionChanged =
      getTrimmed(formData.position) !== getTrimmed(originalFormData.position);
    const descriptionChanged =
      getTrimmed(formData.description) !==
      getTrimmed(originalFormData.description);
    const numberOfUsersChanged =
      getTrimmed(formData.numberOfUsers) !==
      getTrimmed(originalFormData.numberOfUsers);

    const formFieldsChanged =
      nameChanged ||
      userNameChanged ||
      prefixChanged ||
      mobileChanged ||
      emailChanged ||
      passwordChanged ||
      workplaceChanged ||
      positionChanged ||
      descriptionChanged ||
      numberOfUsersChanged;

    // Check if secondary emails have changed (deep comparison with sorting)
    const currentSorted = secondaryEmails.toSorted();
    const originalSorted = originalSecondaryEmails.toSorted();
    const emailsChanged =
      secondaryEmails.length !== originalSecondaryEmails.length ||
      JSON.stringify(currentSorted) !== JSON.stringify(originalSorted);

    return formFieldsChanged || emailsChanged;
  };

  const validateFields = () => {
    let errObj = { ...initialValues };

    if (!formData.name) {
      errObj.name = "This field is required";
    } else if (!/^[A-Za-z0-9\s]+$/.test(formData.name)) {
      errObj.name = "Only alphabets, numbers and spaces are allowed";
    } else {
      errObj.name = "";
    }

    // User Name field is not editable in this form (input is disabled/hidden
    // below), so it can't be validated as required without trapping the user.
    errObj.userName = "";

    if (!formData.email) {
      errObj.email = "This field is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errObj.email = "Email is invalid";
    } else {
      errObj.email = "";
    }

    if (formData.password && formData.password.length < 8) {
      errObj.password = "Password must be at least 8 characters";
    } else {
      errObj.password = "";
    }

    setErrors(errObj);
    const isValid = Object.values(errObj).every((x) => x === "" || x === null);
    return isValid;
  };

  const handleSubmit = () => {
    handleRequestOtp();
  };

  const handleCancel = () => {
    navigate("/profile-details");
  };

  const handleProfile = () => {
    navigate("/profile-details");
  };

  // Auto-verify when all OTP digits are entered.
  // isLoading is intentionally excluded from deps: handleVerifyOtp sets it
  // itself, so depending on it here would re-run/cleanup this effect mid-flight
  // (marking the in-flight call "stale" before its response even arrives) and
  // retrigger verification with the same OTP in an infinite loop. autoSubmitRef
  // guards re-entrancy instead, without causing the effect to re-run.
  useEffect(() => {
    let ignore = false;
    if (otp.length === 6 && showOtpModal && !autoSubmitRef.current) {
      autoSubmitRef.current = true;
      handleVerifyOtp(() => ignore).finally(() => {
        autoSubmitRef.current = false;
      });
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, showOtpModal]);

  const handleRequestOtp = async () => {
    setIsLoading(true);
    if (validateFields()) {
      const payload = {
        name: formData.name,
        userName: formData.userName,
        prefix: formData.prefix,
        mobileNumber: formData.mobileNumber,
        emailId: formData.email,
        workPlace: formData.workplace,
        position: formData.position,
        description: formData.description,
        numberOfUsers: parseInt(formData.numberOfUsers),
        emails: secondaryEmails,
        imageUrl: null,
      };

      // Only send a password when the user explicitly opted to reset it and
      // typed a new one; otherwise leave it out so the backend keeps the
      // existing password untouched.
      if (resetPassword && formData.password?.trim()) {
        payload.password = formData.password;
      }

      // Store payload for later use
      profilePayloadRef.current = payload;

      // Request OTP
      try {
        const res = await RequestOtp();
        setIsLoading(false);
        const { statusCode, message } = res;
        if (statusCode === 200) {
          setShowOtpModal(true);
          setOtp("");
        } else {
          showToast(message || "Failed to request OTP", "error");
        }
      } catch (error) {
        setIsLoading(false);
        showToast(error?.message || "Failed to request OTP", "error");
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (isStale = () => false) => {
    setIsLoading(true);

    if (otp.length !== 6) {
      setIsLoading(false);
      return;
    }

    // Verify OTP with authenticationType = 3
    const verifyPayload = {
      otp,
      authenticationType: 3,
    };

    try {
      const res = await UserVerifyOTP(verifyPayload, formData.email, 3);
      if (isStale()) return;
      const { statusCode, message } = res;
      if (statusCode === 200) {
        // OTP verified, now call UpdateHealthProfessionalProfile
        try {
          const updateRes = await UpdateHealthProfessionalProfile(profilePayloadRef.current);
          if (isStale()) return;
          const { statusCode: updateStatusCode, message: updateMessage } = updateRes;
          if (updateStatusCode === 200) {
            showToast(updateMessage || "Profile updated successfully", "success");
            setShowOtpModal(false);
            setTimeout(() => {
              navigate("/profile-details");
            }, 1500);
          } else {
            showToast(updateMessage || "Failed to update profile", "error");
            setShowOtpModal(false);
            setOtp("");
          }
        } catch (updateError) {
          if (!isStale()) {
            showToast(updateError?.message || "Failed to update profile", "error");
            setShowOtpModal(false);
            setOtp("");
          }
        }
      } else {
        showToast(message || "OTP verification failed", "error");
        setOtp("");
      }
    } catch (error) {
      if (!isStale()) {
        showToast(error?.message || "OTP verification failed", "error");
        setOtp("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOtp = () => {
    setShowOtpModal(false);
    setOtp("");
    profilePayloadRef.current = null;
  };

  const otpModalRef = useRef(null);

  useEffect(() => {
    const dialog = otpModalRef.current;
    if (!dialog) return;
    if (showOtpModal && !dialog.open) {
      dialog.showModal();
    } else if (!showOtpModal && dialog.open) {
      dialog.close();
    }
  }, [showOtpModal]);

  useEffect(() => {
    const dialog = otpModalRef.current;
    if (!dialog) return undefined;
    // Escape triggers the native "cancel" event (not the programmatic
    // close() calls above), so this only fires on user-initiated dismissal —
    // mirror the Cancel button so state stays in sync.
    const handleCancel = () => handleCancelOtp();
    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    errors,
    formData,
    secondaryEmails,
    newSecondaryEmail,
    setNewSecondaryEmail,
    editingIndex,
    editingEmail,
    setEditingEmail,
    isLoading,
    otp,
    setOtp,
    otpModalRef,
    resetPassword,
    onResetPasswordClick,
    handleChange,
    handleAddSecondaryEmail,
    handleDeleteSecondaryEmail,
    handleEditSecondaryEmail,
    handleSaveEditSecondaryEmail,
    handleCancelEditSecondaryEmail,
    isFormChanged,
    handleSubmit,
    handleCancel,
    handleProfile,
    handleVerifyOtp,
    handleCancelOtp,
  };
};

export default useUserForm;
